import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: 'white',
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  qrSection: {
    width: '20%',
  },
  headerText: {
    width: '60%',
    textAlign: 'center',
  },
  wrNumber: {
    width: '20%',
    textAlign: 'right',
    fontSize: 18,
    fontWeight: 'bold',
  },
  title: {
    fontSize: 12,
    marginBottom: 5,
  },
  companyName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 3,
  },
  address: {
    fontSize: 10,
    marginBottom: 2,
  },
  phone: {
    fontSize: 10,
    marginBottom: 10,
  },
  customerSection: {
    flexDirection: 'row',
    marginBottom: 15,
  },
  customerInfo: {
    width: '50%',
  },
  wrhInfo: {
    width: '50%',
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 3,
  },
  text: {
    fontSize: 9,
    marginBottom: 2,
  },
  bold: {
    fontWeight: 'bold',
  },
  table: {
    display: 'flex',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginBottom: 10,
  },
  tableRow: { 
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
  },
  tableRowLast: { 
    flexDirection: 'row',
  },
  tableCol: {
    borderRightWidth: 1,
    borderRightColor: '#000',
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  tableColLast: {
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
    fontSize: 8,
  },
  tableCell: {
    fontSize: 7,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  signatureSection: {
    flexDirection: 'row',
    marginTop: 30,
  },
  signatureBox: {
    width: '45%',
    borderTopWidth: 1,
    borderTopColor: '#000',
    paddingTop: 5,
    marginRight: '10%',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    fontSize: 7,
    textAlign: 'justify',
  }
});

// Create Document Component
function WRPdfDocument({ wrDetails }) {
  // Format date strings to local format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!wrDetails) {
    return <Document><Page size="A4"><Text>No data available</Text></Page></Document>;
  }

  // Calculate totals
  const totalPieces = wrDetails.freight ? wrDetails.freight.reduce((sum, item) => sum + parseInt(item.Pcs || 0), 0) : 0;
  const totalWeight = wrDetails.freight ? wrDetails.freight.reduce((sum, item) => sum + parseInt(item.Wt || 0), 0) : 0;

  // Generate QR code image URL - using an external service for simplicity
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=WR-${wrDetails.idwarehouse}`;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.qrSection}>
            <Image src={qrCodeUrl} style={{ width: 80, height: 80 }} />
          </View>
          <View style={styles.headerText}>
            <Text style={styles.title}>WAREHOUSE RECEIPT NON-NEGOTIABLE</Text>
            <Text style={styles.companyName}>INTERNATIONAL EXPRESS</Text>
            <Text style={styles.address}>141 Robins St</Text>
            <Text style={styles.address}>Lowell, NC 28098</Text>
            <Text style={styles.phone}>704-424-5454</Text>
          </View>
          <View style={styles.wrNumber}>
            <Text>{wrDetails.idwarehouse}</Text>
          </View>
        </View>

        {/* Customer and WRH Info */}
        <View style={styles.customerSection}>
          <View style={styles.customerInfo}>
            <Text style={styles.sectionTitle}>Customer</Text>
            <Text style={styles.text}>{wrDetails.shipper}</Text>
            <Text style={styles.text}>3800 ARCO CORPORATE DR STE 250</Text>
            <Text style={styles.text}>CHARLOTTE, NC 28273</Text>
            <Text style={styles.text}>704-357-0443</Text>
          </View>
          <View style={styles.wrhInfo}>
            <Text style={styles.text}>
              <Text style={styles.bold}>Booking/IT#: </Text>
              {wrDetails.bookingNum}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Date of Entry: </Text>
              {formatDate(wrDetails.entryDate)}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Date of Receipt: </Text>
              {formatDate(wrDetails.recDate)}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Shipper: </Text>
              {wrDetails.shipper}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Consignee: </Text>
              {wrDetails.consignee}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Destination: </Text>
              {wrDetails.destination || 'IMPORT'}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Carrier: </Text>
              {wrDetails.carrier || 'IET'}
              <Text style={styles.bold}>    Pro: </Text>
              {wrDetails.proNum}
            </Text>
          </View>
        </View>

        {/* Freight Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <View style={[styles.tableCol, { width: '8%' }]}>
              <Text>FID</Text>
            </View>
            <View style={[styles.tableCol, { width: '16%' }]}>
              <Text>Customer Ref</Text>
            </View>
            <View style={[styles.tableCol, { width: '14%' }]}>
              <Text>MAWB</Text>
            </View>
            <View style={[styles.tableCol, { width: '14%' }]}>
              <Text>HAWB</Text>
            </View>
            <View style={[styles.tableCol, { width: '18%' }]}>
              <Text>Description</Text>
            </View>
            <View style={[styles.tableCol, { width: '10%' }]}>
              <Text>Dims</Text>
            </View>
            <View style={[styles.tableCol, { width: '8%' }]}>
              <Text>Pcs</Text>
            </View>
            <View style={[styles.tableColLast, { width: '12%' }]}>
              <Text>Wt</Text>
            </View>
          </View>

          {/* Table Rows */}
          {wrDetails.freight && wrDetails.freight.map((item, index) => (
            <View key={index} style={index === wrDetails.freight.length - 1 ? styles.tableRowLast : styles.tableRow}>
              <View style={[styles.tableCol, { width: '8%' }]}>
                <Text style={styles.tableCell}>{item.FID}</Text>
              </View>
              <View style={[styles.tableCol, { width: '16%' }]}>
                <Text style={styles.tableCell}>{item.CustomerRef || '-'}</Text>
              </View>
              <View style={[styles.tableCol, { width: '14%' }]}>
                <Text style={styles.tableCell}>{item.MAWB || '-'}</Text>
              </View>
              <View style={[styles.tableCol, { width: '14%' }]}>
                <Text style={styles.tableCell}>{item.HAWB || '-'}</Text>
              </View>
              <View style={[styles.tableCol, { width: '18%' }]}>
                <Text style={styles.tableCell}>{item.Notes || 'PALLET'}</Text>
              </View>
              <View style={[styles.tableCol, { width: '10%' }]}>
                <Text style={styles.tableCell}>32x48x40</Text>
              </View>
              <View style={[styles.tableCol, { width: '8%' }]}>
                <Text style={styles.tableCell}>{item.Pcs}</Text>
              </View>
              <View style={[styles.tableColLast, { width: '12%' }]}>
                <Text style={styles.tableCell}>{item.Wt}</Text>
              </View>
            </View>
          ))}

          {/* Totals Row */}
          <View style={styles.tableRow}>
            <View style={[styles.tableCol, { width: '80%' }]}>
              <Text style={[styles.tableCell, styles.bold]}>Total</Text>
            </View>
            <View style={[styles.tableCol, { width: '8%' }]}>
              <Text style={[styles.tableCell, styles.bold]}>{totalPieces}</Text>
            </View>
            <View style={[styles.tableColLast, { width: '12%' }]}>
              <Text style={[styles.tableCell, styles.bold]}>{totalWeight}</Text>
            </View>
          </View>
        </View>

        {/* Signature Section */}
        <View style={styles.signatureSection}>
          <View style={styles.signatureBox}>
            <Text style={styles.sectionTitle}>Received By:_____________________</Text>
            <Text style={styles.text}>Received in good order unless otherwise stated</Text>
            <Text style={styles.text}></Text>
            <Text style={styles.text}>Name:_________________________</Text>
            <Text style={styles.text}></Text>
            <Text style={styles.text}>Date/Time:_____________________</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.sectionTitle}>Consignee:_____________________</Text>
            <Text style={styles.text}>Received in good order unless otherwise stated</Text>
            <Text style={styles.text}></Text>
            <Text style={styles.text}>Name:_________________________</Text>
            <Text style={styles.text}></Text>
            <Text style={styles.text}>Date/Time:_____________________</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>
            Warehouse Liability Statement: If you're accepting this receipt, you are accepting liability based on standard warehouse terms and conditions in accordance with normal warehouse receipt legal terms as published in Uniform Commercial Code Section 7-204(2).Warehouse Legal Liability Insurance provides protection in the event that negligence on your part results in damages to someone else's property. According to Uniform Commercial Code Section 7-204(2), "Damages may be limited by a term in the warehouse receipt or storage agreement limiting the amount of liability in case of loss or damage, and setting forth a specific liability per article or item, or value per unit of weight, beyond which the warehouse owner shall not be liable." This means that your contract must state the value of what is placed in your storage facility to determine damages in the event of a loss.
          </Text>
        </View>
      </Page>
    </Document>
  );
}

export default WRPdfDocument; 